<template>
  <!-- Hero Section -->
  <div class="relative min-h-screen overflow-hidden">
    <!-- Background Image -->
    <div
      class="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
      :style="{
        backgroundImage: `url(${require('@/assets/images/foundation3.jpg')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }"
    ></div>

    <!-- Overlay -->
    <div class="absolute inset-0 bg-black bg-opacity-75"></div>

    <!-- Floating Orbs -->
    <div class="absolute top-20 left-4 sm:left-10 w-24 h-24 sm:w-32 sm:h-32 bg-black bg-opacity-10 rounded-full blur-xl animate-float"></div>
    <div class="absolute top-40 right-4 sm:right-20 w-16 h-16 sm:w-24 sm:h-24 bg-black bg-opacity-20 rounded-full blur-lg animate-float-delayed"></div>
    <div class="absolute bottom-40 left-1/4 w-32 h-32 sm:w-40 sm:h-40 bg-black bg-opacity-15 rounded-full blur-2xl animate-float-slow"></div>

    <!-- Hero Content -->
    <div class="relative z-10 flex min-h-screen items-center justify-center px-4 sm:px-6 lg:px-8">
      <div class="text-center max-w-7xl mx-auto w-full">
        <div class="mb-6 sm:mb-8 mt-20 sm:mt-32 lg:mt-40">
          <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight px-2">
            <span class="typing-container" v-html="displayedText"></span>
            <span class="border-r-2 border-white animate-pulse ml-1">&nbsp;</span>
          </h1>
        </div>

        <!-- Subtitle -->
        <p class="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 mb-8o sm:mb-12 animate-fade-in-up animation-delay-600 max-w-4xl mx-auto leading-relaxed px-4">
          Empowering communities through education, opportunity, and hope.
          <span class="text-blue-500 font">Together we build brighter futures.</span>
        </p>

        <!-- CTA Buttons -->
        <div class="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center animate-fade-in-up animation-delay-800 px-4">
          <router-link to="/about-us" class="w-full sm:w-auto">
            <button class="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-yellow text-white font-semibold rounded-full shadow-xl hover:shadow-blue/25 transform hover:scale-105 transition-all duration-300 min-w-[200px] text-sm sm:text-base">
              Learn Our Story
            </button>
          </router-link>
          <router-link to="/pitch-your-dream" class="w-full sm:w-auto">
            <button class="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 border-2 border-white border-opacity-30 text-white font-semibold rounded-full hover:bg-white hover:bg-opacity-10 backdrop-blur-sm transition-all duration-300 min-w-[200px] text-sm sm:text-base">
              Explore Programs
            </button>
          </router-link>
        </div>

        <!-- Stats -->
        <div class="mt-12 mb-12 sm:mt-16 lg:mt-20 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 animate-fade-in-up animation-delay-1000 px-4">
          <div class="text-center p-4 sm:p-6 backdrop-blur-sm bg-white bg-opacity-5 rounded-2xl border border-white border-opacity-10">
            <div class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">1000+</div>
            <div class="text-gray-300 text-sm sm:text-base">Lives Impacted</div>
          </div>
          <div class="text-center p-4 sm:p-6 backdrop-blur-sm bg-white bg-opacity-5 rounded-2xl border border-white border-opacity-10">
            <div class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">50+</div>
            <div class="text-gray-300 text-sm sm:text-base">Scholarships Awarded</div>
          </div>
          <div class="text-center p-4 sm:p-6 backdrop-blur-sm bg-white bg-opacity-5 rounded-2xl border border-white border-opacity-10">
            <div class="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">10+</div>
            <div class="text-gray-300 text-sm sm:text-base">Communities Served</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
      <div class="w-6 h-10 border-2 border-white border-opacity-30 rounded-full flex justify-center">
        <div class="w-1 h-3 bg-white bg-opacity-60 rounded-full mt-2 animate-pulse"></div>
      </div>
    </div>
  </div>

  <OurHistory />
  <RecentIntiative />
  <Insights />
</template>

<script>
import Insights from "@/components/Insights.vue";
import OurHistory from "@/components/OurHistory.vue";
import RecentIntiative from "@/components/RecentIntiative.vue";

export default {
  name: "HomeView",
  components: {
    OurHistory,
    RecentIntiative,
    Insights,
  },
  data() {
    return {
      properties: 0,
      engineers: 0,
      staff: 0,
      location: 0,
      realty: 0,
      displayedText: "",
      currentIndex: 0,
      isErasing: false,
      currentCharIndex: 0,
      speed: 100,
      delayBetweenWords: 1800,
      texts: [
        'IMPACTING <span class="text-blue-500">LIVES</span> POSITIVELY!',
        'BUILDING <span class="text-blue-500">FUTURES</span> TOGETHER!',
        'CREATING <span class="text-blue-500">HOPE</span> FOR ALL!'
      ],
    };
  },
  mounted() {
    this.fetchStats();
    this.typingEffect();
  },
  methods: {
    fetchStats() {
      setTimeout(() => {
        this.properties = 100;
        this.engineers = 50;
        this.staff = 30;
        this.location = 5;
        this.realty = 200;
      }, 1000);
    },
    
    // Helper method to parse HTML and extract characters with their styling
    parseTextWithHTML(htmlString) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlString;
      
      const result = [];
      const traverse = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent;
          for (let i = 0; i < text.length; i++) {
            result.push({
              char: text[i],
              html: text[i]
            });
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          const openTag = `<${node.tagName.toLowerCase()}${node.className ? ` class="${node.className}"` : ''}>`;
          const closeTag = `</${node.tagName.toLowerCase()}>`;
          
          for (let child of node.childNodes) {
            if (child.nodeType === Node.TEXT_NODE) {
              const text = child.textContent;
              for (let i = 0; i < text.length; i++) {
                result.push({
                  char: text[i],
                  html: openTag + text.substring(0, i + 1) + closeTag
                });
              }
            } else {
              traverse(child);
            }
          }
        }
      };
      
      traverse(tempDiv);
      return result;
    },
    
    // Simplified approach - build HTML progressively
    buildProgressiveHTML(htmlString, targetLength) {
      let plainText = htmlString.replace(/<[^>]*>/g, '');
      if (targetLength >= plainText.length) {
        return htmlString;
      }
      
      let result = '';
      let plainIndex = 0;
      let i = 0;
      
      while (i < htmlString.length && plainIndex < targetLength) {
        if (htmlString[i] === '<') {
          // Find the end of the tag
          let tagEnd = htmlString.indexOf('>', i);
          if (tagEnd !== -1) {
            result += htmlString.substring(i, tagEnd + 1);
            i = tagEnd + 1;
          } else {
            break;
          }
        } else {
          result += htmlString[i];
          plainIndex++;
          i++;
        }
      }
      
      // Close any open tags
      const openTags = [];
      const tagRegex = /<\/?([^>]+)>/g;
      let match;
      
      while ((match = tagRegex.exec(result)) !== null) {
        const tagName = match[1].split(' ')[0];
        if (match[0].startsWith('</')) {
          // Closing tag
          const index = openTags.lastIndexOf(tagName);
          if (index !== -1) {
            openTags.splice(index, 1);
          }
        } else if (!match[0].endsWith('/>')) {
          // Opening tag
          openTags.push(tagName);
        }
      }
      
      // Close remaining open tags
      for (let j = openTags.length - 1; j >= 0; j--) {
        result += `</${openTags[j]}>`;
      }
      
      return result;
    },
    
    typingEffect() {
      const currentText = this.texts[this.currentIndex];
      const plainText = currentText.replace(/<[^>]*>/g, '');
      
      if (!this.isErasing) {
        // Typing forward
        if (this.currentCharIndex < plainText.length) {
          this.displayedText = this.buildProgressiveHTML(currentText, this.currentCharIndex + 1);
          this.currentCharIndex++;
          setTimeout(this.typingEffect, this.speed);
        } else {
          // Finished typing, wait then start erasing
          setTimeout(() => {
            this.isErasing = true;
            this.typingEffect();
          }, this.delayBetweenWords);
        }
      } else {
        // Erasing backward
        if (this.currentCharIndex > 0) {
          this.currentCharIndex--;
          this.displayedText = this.buildProgressiveHTML(currentText, this.currentCharIndex);
          setTimeout(this.typingEffect, this.speed / 2); // Erase faster
        } else {
          // Finished erasing, move to next text
          this.isErasing = false;
          this.currentIndex = (this.currentIndex + 1) % this.texts.length;
          this.displayedText = "";
          setTimeout(this.typingEffect, this.speed);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* Animations */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}
.animate-float-delayed {
  animation: float 6s ease-in-out infinite;
  animation-delay: 2s;
}
.animate-float-slow {
  animation: float 8s ease-in-out infinite;
  animation-delay: 1s;
}
.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}
.animation-delay-600 {
  animation-delay: 0.6s;
}
.animation-delay-800 {
  animation-delay: 0.8s;
}
.animation-delay-1000 {
  animation-delay: 1s;
}
</style>
